import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { scrollToSection } from "@/lib/lenisStore";

// `id` scrolls to a homepage section (from another page it goes to /#id); `to` is its own page.
const NAV_LINKS = [
  { label: "Services", id: "services" },
  { label: "Gallery", id: "gallery", to: "/gallery" },
  { label: "About", id: "about" },
  { label: "Reviews", id: "testimonials" },
];

const linkCls = (on) => `whitespace-nowrap text-sm font-medium transition-colors ${on ? "text-navy" : "text-muted-foreground hover:text-navy"}`;

export const Navbar = ({ onBook }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const go = (id) => {
    setOpen(false);
    if (document.getElementById(id)) scrollToSection(id);
    else navigate(`/#${id}`);
  };

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-background/85 border-b border-border/60"
    >
      <div className="px-4 sm:px-8 xl:px-16 mx-auto max-w-7xl h-[72px] flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo-link" aria-label="Solid Home Services home" className="shrink-0">
          <Logo large />
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-8" data-testid="nav-links">
          {NAV_LINKS.map((l) => (l.to ? (
            <Link key={l.id} to={l.to} data-testid={`nav-link-${l.id}`} aria-current={pathname === l.to ? "page" : undefined}
              className={linkCls(pathname === l.to)}>
              {l.label}
            </Link>
          ) : (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className={linkCls(false)}
            >
              {l.label}
            </button>
          )))}
          <Link
            to="/portal"
            data-testid="nav-portal-link"
            className="whitespace-nowrap text-sm font-medium text-muted-foreground hover:text-navy transition-colors"
          >
            Customer Portal
          </Link>
        </nav>

        <div className="hidden lg:flex items-center gap-4 xl:gap-5">
          <Button
            data-testid="nav-book-button"
            data-book-cta
            onClick={() => onBook(null)}
            className="bg-terracotta hover:bg-terracotta-hover text-white font-semibold px-4 xl:px-6 hover:-translate-y-0.5 hover:shadow-md transition-[transform,box-shadow,background-color]"
          >
            Book a Service
          </Button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-navy"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border bg-background px-6 py-4 space-y-3" data-testid="nav-mobile-menu">
          {NAV_LINKS.map((l) => (l.to ? (
            <Link key={l.id} to={l.to} data-testid={`nav-mobile-link-${l.id}`} onClick={() => setOpen(false)}
              aria-current={pathname === l.to ? "page" : undefined}
              className={`block w-full text-left py-2 text-base font-medium ${pathname === l.to ? "text-terracotta" : "text-foreground"}`}>
              {l.label}
            </Link>
          ) : (
            <button
              key={l.id}
              data-testid={`nav-mobile-link-${l.id}`}
              onClick={() => go(l.id)}
              className="block w-full text-left py-2 text-base font-medium text-foreground"
            >
              {l.label}
            </button>
          )))}
          <Link
            to="/portal"
            data-testid="nav-mobile-portal-link"
            onClick={() => setOpen(false)}
            className="block w-full text-left py-2 text-base font-medium text-foreground"
          >
            Customer Portal
          </Link>
          <Button
            data-testid="nav-mobile-book-button"
            data-book-cta
            onClick={() => {
              setOpen(false);
              onBook(null);
            }}
            className="w-full bg-terracotta hover:bg-terracotta-hover text-white font-semibold"
          >
            Book a Service
          </Button>
        </div>
      )}
    </header>
  );
};
